import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  CircularProgress,
  Typography,
  Tabs,
  Tab
} from '@mui/material';

import ResearchJobsTable from './ResearchJobsTable';
import ResearchResults from './ResearchResults';
import FilterResults from '../FilterResults';
import ActivityHistoryTour from '../onboarding/ActivityHistoryTour';
import WyspurLoadingSpinner from '../common/WyspurLoadingSpinner';
import { useAuth } from '../../context/AuthContext';
import { apiGet, apiPost, apiDelete } from '../../utils/apiUtils';
import { createLogger } from '../../utils/debugUtils';
import supabase from '../../utils/supabaseClient';

const logger = createLogger('ResearchAgentActivityHistory');

/**
 * ResearchAgentActivityHistory Component
 * Dedicated page for viewing research job history and results
 */
const ResearchAgentActivityHistory = ({ initialTab = 0, onTabChange }) => {
  useAuth(); // Keep auth context for future use
  
  // Removed unused jobsListRefreshRef
  
  // State for jobs, loading, and error handling
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDelayedContent, setShowDelayedContent] = useState(false);
  const [qualifiedCounts, setQualifiedCounts] = useState({});
  
  // Tab state for switching between Research and List Cleaner
  const [activeTab, setActiveTab] = useState(initialTab); // 0 = Research, 1 = List Cleaner
  
  // Results view state
  const [viewingResults, setViewingResults] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [jobResults, setJobResults] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  
  // Tutorial state
  const [showTour, setShowTour] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [hasSeenTutorial, setHasSeenTutorial] = useState(false);
  
  // Ref for FilterResults component
  const filterResultsRef = useRef();
  
  /**
   * Calculate qualified count using same logic as FilterResults component
   * Copied from ResearchJobsTable for pre-calculation
   */
  const calculateQualifiedCount = async (job) => {
    // Only calculate for filter jobs (List Cleaner)
    if (!job.jobId) {
      return { qualified: 0, total: job.totalRows || 0 };
    }

    try {
      // Use EXACT same pattern as ResearchAgentActivityHistory.js for filter jobs
      const response = await apiGet(`/api/results/${job.jobId}`);
      
      // Handle response the same way as ResearchAgentActivityHistory.js
      let responseData;
      if (response instanceof Response) {
        if (!response.ok) {
          throw new Error(`API returned status ${response.status}`);
        }
        responseData = await response.json();
      } else {
        // Handle case where apiGet might already return parsed data
        responseData = response;
      }
      
      // Use responseData as filterResults (same as ResearchAgentActivityHistory.js)
      const filterResults = responseData;
      
      if (!filterResults || !filterResults.keptData || !filterResults.excludedData) {
        return { qualified: 0, total: job.totalRows || 0 };
      }

      // Use same logic as FilterResults component
      const logicMode = filterResults.logicMode || 'AND';
      const selectedColumns = filterResults.selectedColumns || [];
      const llmValueDecisions = filterResults.llmValueDecisions || {};

      if (!selectedColumns.length) {
        return {
          qualified: filterResults.stats?.keptRowCount || 0,
          total: filterResults.stats?.totalRows || 0
        };
      }

      // Get all original data (kept + excluded)
      const allData = [...(filterResults.keptData || []), ...(filterResults.excludedData || [])];
      
      let qualifiedCount = 0;
      let disqualifiedCount = 0;

      // Apply logic to each row (same logic as FilterResults)
      allData.forEach(row => {
        let keepRow = logicMode === 'OR' ? false : true; // Start with false for OR, true for AND

        selectedColumns.forEach(column => {
          const value = row[column];
          
          // Get decision from LLM decisions (no local overrides in job table context)
          const decision = (llmValueDecisions[column] && llmValueDecisions[column][value]) ? 
            llmValueDecisions[column][value].decision : 'EXCLUDE';

          // Apply logic
          if (logicMode === 'AND') {
            // AND logic: any EXCLUDE decision disqualifies the row
            if (decision === 'EXCLUDE') {
              keepRow = false;
            }
          } else if (logicMode === 'OR') {
            // OR logic: any KEEP decision qualifies the row
            if (decision === 'KEEP') {
              keepRow = true;
            }
          }
        });

        if (keepRow) {
          qualifiedCount++;
        } else {
          disqualifiedCount++;
        }
      });

      const result = {
        qualified: qualifiedCount,
        total: allData.length
      };
      
      console.log(`[QualifiedCount] Pre-calculated for job ${job.jobId}:`, result);
      return result;
    } catch (error) {
      console.error(`Error calculating qualified count for job ${job.jobId}:`, error);
      return { qualified: 0, total: job.totalRows || 0 };
    }
  };
  
  /**
   * Fetch jobs from the API based on active tab
   */
  const fetchJobs = useCallback(async () => {
    try {
      // Fetch from different endpoints based on active tab
      const endpoint = activeTab === 0 ? '/api/research/jobs' : '/api/filter/jobs';
      const response = await apiGet(endpoint);
      
      // Check if response is a Response object and parse it
      if (response instanceof Response) {
        if (!response.ok) {
          throw new Error(`API returned status ${response.status}`);
        }
        const data = await response.json();
        if (Array.isArray(data)) {
          // Normalize job data and ensure createdAt is directly accessible at top level
          const normalizedJobs = data.map(job => ({
            ...job,
            // Add any custom mappings or transformations here if needed
          }));
          
          setJobs(normalizedJobs);
          
          // Pre-calculate qualified counts for List Cleaner tab
          if (activeTab === 1 && normalizedJobs.length > 0) {
            console.log('[PreCalculation] Starting qualified count calculation for', normalizedJobs.length, 'filter jobs');
            const counts = {};
            
            // Calculate qualified counts for all jobs in parallel
            const promises = normalizedJobs.map(async (job) => {
              const { qualified, total } = await calculateQualifiedCount(job);
              counts[job.jobId] = { qualified, total };
            });
            
            Promise.all(promises).then(() => {
              console.log('[PreCalculation] Completed qualified count calculation');
              setQualifiedCounts(counts);
            }).catch(error => {
              console.error('[PreCalculation] Error calculating qualified counts:', error);
              setQualifiedCounts({});
            });
          } else if (activeTab === 0) {
            // Clear qualified counts when switching to Research tab
            setQualifiedCounts({});
          }
        } else {
          logger.warn('Invalid jobs data format (not an array)', data);
          setJobs([]);
        }
      } else if (Array.isArray(response)) {
        // Handle case where apiGet might already return parsed data
        setJobs(response);
        
        // Pre-calculate qualified counts for List Cleaner tab
        if (activeTab === 1 && response.length > 0) {
          console.log('[PreCalculation] Starting qualified count calculation for', response.length, 'filter jobs (direct response)');
          const counts = {};
          
          // Calculate qualified counts for all jobs in parallel
          const promises = response.map(async (job) => {
            const { qualified, total } = await calculateQualifiedCount(job);
            counts[job.jobId] = { qualified, total };
          });
          
          Promise.all(promises).then(() => {
            console.log('[PreCalculation] Completed qualified count calculation (direct response)');
            setQualifiedCounts(counts);
          }).catch(error => {
            console.error('[PreCalculation] Error calculating qualified counts (direct response):', error);
            setQualifiedCounts({});
          });
        } else if (activeTab === 0) {
          // Clear qualified counts when switching to Research tab
          setQualifiedCounts({});
        }
      } else {
        logger.warn('Invalid jobs data format', response);
        setJobs([]);
      }
    } catch (error) {
      logger.error('Failed to fetch jobs:', error);
      setJobs([]);
      setError('Failed to load jobs. Please try again later.');
    }
  }, [activeTab]); // Add activeTab as dependency


  // Fetch jobs on component mount and set up auto-refresh
  useEffect(() => {
    // Initial fetch
    fetchJobs();
    
    // Set up auto-refresh every 5 seconds
    const REFRESH_INTERVAL = 5000; // 5 seconds in milliseconds
    const intervalId = setInterval(() => {
      fetchJobs();
    }, REFRESH_INTERVAL);
    
    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [fetchJobs]);

  // Handle 4-second delay for content display on initial load and tab changes
  useEffect(() => {
    console.log('WyspurSpinner: Starting 4-second delay, activeTab:', activeTab);
    setShowDelayedContent(false);
    const timer = setTimeout(() => {
      console.log('WyspurSpinner: 4-second delay complete, showing content');
      setShowDelayedContent(true);
    }, 4000); // 4 second delay to cover all data loading
    
    return () => {
      console.log('WyspurSpinner: Cleaning up timer');
      clearTimeout(timer);
    };
  }, [activeTab]); // Reset timer when tab changes

  // Also trigger delay on initial component mount
  useEffect(() => {
    console.log('WyspurSpinner: Component mounted, triggering initial delay');
    setShowDelayedContent(false);
    const timer = setTimeout(() => {
      console.log('WyspurSpinner: Initial 4-second delay complete');
      setShowDelayedContent(true);
    }, 4000);
    
    return () => clearTimeout(timer);
  }, []); // Run only on mount

  /**
   * Handle pausing a job
   */
  const handlePauseJob = async (job) => {
    try {
      setLoading(true);
      // Use different endpoints based on active tab
      const endpoint = activeTab === 0 
        ? `/api/research/jobs/${job.jobId}/pause`
        : `/api/filter/jobs/${job.jobId}/pause`;
      
      const response = await apiPost(endpoint);
      
      // Check if response is a Response object and handle it
      if (response instanceof Response) {
        if (!response.ok) {
          throw new Error(`API returned status ${response.status}`);
        }
        // Parse the response if needed
        await response.text();
      }
      
      await fetchJobs(); // Refresh jobs after action
    } catch (error) {
      logger.error(`Failed to pause job ${job.jobId}:`, error);
      setError(`Failed to pause job: ${job.name}. Please try again.`);
      setLoading(false);
    }
  };

  /**
   * Handle resuming a job
   */
  const handleResumeJob = async (job) => {
    try {
      setLoading(true);
      // Use different endpoints based on active tab
      const endpoint = activeTab === 0 
        ? `/api/research/jobs/${job.jobId}/resume`
        : `/api/filter/jobs/${job.jobId}/resume`;
      
      const response = await apiGet(endpoint, { method: 'POST' });
      
      // Check if response is a Response object and handle it
      if (response instanceof Response) {
        if (!response.ok) {
          throw new Error(`API returned status ${response.status}`);
        }
        // Parse the response if needed
        await response.text();
      }
      
      await fetchJobs(); // Refresh jobs after action
    } catch (error) {
      logger.error(`Failed to resume job ${job.jobId}:`, error);
      setError(`Failed to resume job: ${job.name}. Please try again.`);
      setLoading(false);
    }
  };

  /**
   * Handle deleting a job
   */
  const handleDeleteJob = async (job) => {
    if (window.confirm(`Are you sure you want to delete the job: ${job.name}?`)) {
      try {
        setLoading(true);
        // Use different endpoints based on active tab
        const endpoint = activeTab === 0 
          ? `/api/research/jobs/${job.jobId}`
          : `/api/filter/jobs/${job.jobId}`;
        
        const response = await apiDelete(endpoint);
        
        // Check if response is a Response object and handle it
        if (response instanceof Response) {
          if (!response.ok) {
            throw new Error(`API returned status ${response.status}`);
          }
          // Parse the response if needed
          await response.text();
        }
        
        logger.info(`Successfully deleted job ${job.jobId}`);
        // Refresh jobs after successful deletion
        await fetchJobs();
      } catch (error) {
        logger.error(`Failed to delete job ${job.jobId}:`, error);
        setError(`Failed to delete job: ${job.name}. Please try again.`);
        setLoading(false);
      }
    }
  };

  /**
   * Fetch job results when a job is selected for viewing
   */
  const handleViewResults = async (job) => {
    const jobId = job.jobId;
    if (!jobId) return;
    
    setSelectedJobId(jobId);
    setResultsLoading(true);
    setViewingResults(true);
    
    try {
      // Use different endpoints based on active tab
      const endpoint = activeTab === 0 
        ? `/api/research/jobs/${jobId}/results`  // Research jobs
        : `/api/results/${jobId}`;               // Filter jobs
      
      const response = await apiGet(endpoint);
      
      // Check if response is a Response object and parse it
      let websiteResults = [];
      let responseData;
      
      if (response instanceof Response) {
        if (!response.ok) {
          throw new Error(`API returned status ${response.status}`);
        }
        responseData = await response.json();
      } else {
        // Handle case where apiGet might already return parsed data
        responseData = response;
      }
      
      // Handle different response formats based on job type
      if (activeTab === 0) {
        // Research job results - expects websites array
        if (Array.isArray(responseData)) {
          // API returns an array of website results
          websiteResults = responseData;
          logger.info(`Loaded ${websiteResults.length} results for job ${jobId}`);
          
          // Format the results as expected by ResearchResults component
          // Include job metadata for Research Criteria tab
          const formattedResults = {
            websites: websiteResults,
            completedAt: job.completedAt || new Date().toISOString(),
            job: {
              instructions: job.instructions,
              column_name: job.columnName,
              enable_web_search: job.enableWebSearch,
              name: job.name
            }
          };
          
          logger.info(`Formatted results for ResearchResults component`, {
            websitesCount: formattedResults.websites.length
          });
          
          setJobResults(formattedResults);
        } else if (responseData && responseData.websites) {
          // API directly returns the expected format with websites array
          logger.info(`Loaded ${responseData.websites.length} results for job ${jobId}`);
          setJobResults(responseData);
        } else {
          // No valid results
          logger.warn(`No valid results found for job ${jobId}`, responseData);
          setJobResults({
            websites: [],
            completedAt: job.completedAt || new Date().toISOString()
          });
        }
      } else {
        // Filter job results - expects keptData, excludedData, etc.
        if (responseData && (responseData.keptData || responseData.excludedData)) {
          logger.info(`Loaded filter results for job ${jobId}:`, {
            keptCount: responseData.keptData?.length || 0,
            excludedCount: responseData.excludedData?.length || 0
          });
          setJobResults(responseData);
        } else {
          // No valid filter results
          logger.warn(`No valid filter results found for job ${jobId}`, responseData);
          setJobResults({
            keptData: [],
            excludedData: [],
            stats: { totalRows: 0, keptRowCount: 0, excludedRowCount: 0 }
          });
        }
      }
    } catch (error) {
      logger.error(`Error loading results for job ${jobId}:`, error);
      
      // Set appropriate fallback based on job type
      if (activeTab === 0) {
        setJobResults({
          websites: [],
          completedAt: job.completedAt || new Date().toISOString()
        });
        setError(`Failed to load research results. Please try again later.`);
      } else {
        setJobResults({
          keptData: [],
          excludedData: [],
          stats: { totalRows: 0, keptRowCount: 0, excludedRowCount: 0 }
        });
        setError(`Failed to load filter results. Please try again later.`);
      }
    } finally {
      setResultsLoading(false);
    }
  };

  /**
   * Go back from results view to jobs list
   */
  const handleBackToJobs = () => {
    setViewingResults(false);
    setSelectedJobId(null);
    setJobResults(null);
  };

  // Clear any errors when component unmounts or when changing views
  useEffect(() => {
    return () => setError(null);
  }, [viewingResults]);

  // Clear error when user returns to see the component
  useEffect(() => {
    const handleFocus = () => setError(null);
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Check if should show tutorial on mount when jobs are loaded
  useEffect(() => {
    const checkTutorial = async () => {
      // Only check tutorial if we have jobs and are not viewing results
      if (jobs.length === 0 || viewingResults) return;
      
      // Check if there are any completed jobs
      const hasCompletedJobs = jobs.some(job => job.status.toLowerCase() === 'completed');
      if (!hasCompletedJobs) return;
      
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) return;
      
      const { data } = await supabase
        .from('user_profiles')
        .select('hide_activity_history_tutorial')
        .eq('id', user.id)
        .single();
      
      // Show tutorial if not hidden in DB AND not seen in this session
      if (!data?.hide_activity_history_tutorial && !hasSeenTutorial) {
        setTimeout(() => setShowTour(true), 500);
      }
    };
    
    checkTutorial();
  }, [jobs, viewingResults, hasSeenTutorial]);

  // Handle tutorial finish
  const handleTourFinish = async () => {
    setShowTour(false);
    setHasSeenTutorial(true); // Prevent re-trigger in this session
    
    // Save preference if "don't show again" is checked
    if (dontShowAgain) {
      const user = (await supabase.auth.getUser()).data.user;
      if (user) {
        await supabase
          .from('user_profiles')
          .update({ hide_activity_history_tutorial: true })
          .eq('id', user.id);
      }
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 'calc(100vw - 48px)', overflow: 'hidden', mx: 'auto', boxSizing: 'border-box' }}>
      {/* Add tutorial */}
      <ActivityHistoryTour 
        run={showTour} 
        onFinish={handleTourFinish}
        dontShowAgain={dontShowAgain}
        setDontShowAgain={setDontShowAgain}
      />
      
      {/* Error message display */}
      {error && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
          <Typography variant="body2">{error}</Typography>
        </Box>
      )}
      
      
      {/* Results View or Jobs List */}
      {viewingResults ? (
        resultsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper 
            elevation={0}
            sx={{ 
              p: 0, 
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              width: '100%',
              boxSizing: 'border-box',
              margin: '0 auto',
              border: 'none'
            }}
          >
            <Box sx={{ 
              overflow: 'auto',
              flex: 1,
              width: '100%',
              maxWidth: '100%',
              '&::-webkit-scrollbar': {
                width: '8px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: '4px'
              }
            }}>
              <Paper elevation={0} sx={{ p: 3, width: '100%', maxWidth: '100%', overflow: 'hidden', border: 'none' }}>
                {activeTab === 0 ? (
                  <ResearchResults 
                    jobId={selectedJobId}
                    results={jobResults}
                    onBackToLists={handleBackToJobs}
                  />
                ) : (
                  <FilterResults 
                    ref={filterResultsRef}
                    filterId={selectedJobId}
                    filterResults={jobResults}
                    onBack={handleBackToJobs}
                  />
                )}
              </Paper>
            </Box>
          </Paper>
        )
      ) : (
        <Paper 
          elevation={0}
          sx={{ 
            p: 0, 
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            width: '100%',
            boxSizing: 'border-box',
            margin: '0 auto',
            border: 'none'
          }}
        >
          <Box sx={{ 
            overflow: 'auto',
            flex: 1,
            width: '100%',
            maxWidth: '100%',
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '4px'
            }
          }}>
            <Paper elevation={0} sx={{ p: 3, width: '100%', maxWidth: '100%', overflow: 'hidden', border: 'none' }}>
              {/* Tabs for switching between Research and List Cleaner */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs 
                  id="activity-tabs"
                  value={activeTab} 
                  onChange={(event, newValue) => {
                    setActiveTab(newValue);
                    // Clear the initial tab signal after user manually changes tabs
                    if (onTabChange) onTabChange();
                  }}
                  aria-label="Job type tabs"
                >
                  <Tab label="Website Analyzer" />
                  <Tab label="List Cleaner" />
                </Tabs>
              </Box>
              
              {!showDelayedContent ? (
                (() => {
                  console.log('WyspurSpinner: Rendering spinner, showDelayedContent:', showDelayedContent);
                  return (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center',
                      minHeight: 400
                    }}>
                      <WyspurLoadingSpinner />
                    </Box>
                  );
                })()
              ) : loading && jobs.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : jobs.length === 0 && !loading ? (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    {activeTab === 0 
                      ? 'No research jobs found. Create a new research job to get started.'
                      : 'No list cleaner jobs found. Create a new list to get started.'}
                  </Typography>
                </Box>
              ) : (
                <ResearchJobsTable 
                  jobs={jobs}
                  isLoading={loading}
                  onViewResults={handleViewResults}
                  onPauseJob={handlePauseJob}
                  onResumeJob={handleResumeJob}
                  onDeleteJob={handleDeleteJob}
                  isFilterJob={activeTab === 1}
                  qualifiedCounts={qualifiedCounts}
                />
              )}
            </Paper>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default ResearchAgentActivityHistory;
